import React from "react";
import {Box, Typography} from "@mui/material";
import {ElevationApp} from "../../elevationTool/ElevationApp";
import {MdSelection, selectionGroupOf} from "../../elevationTool/model/view/MdViewSettings";
import {useEventEmitter2} from "../../elevationTool/util/useEventEmitter2";
import {findItem, findStickById, pieceStickId} from "../../elevationTool/scenes/frameGrid/BlockOps";
import {stickThicknessVar} from "../../elevationTool/scenes/frameGrid/Util";
import {
    glazingLabels,
    itemGlazing,
    itemPanelMaterial,
    itemPanelPosition,
    itemProfile,
    panelMaterialLabels,
    panelPositionLabels,
    sticksById,
} from "../../elevationTool/model/frame/MdFrameBlock";
import {frameProfileLabels, slotOfCell} from "../../elevationTool/model/frame/MdFrameSpec";
import {doorLiteGlass, glassBeadLabels, glassTypeLabels, liteGlass, MdGlassResolved} from "../../elevationTool/model/glass/MdGlass";

type Fact = {label: string; value: string};

type Format = (value: number) => string;

// the read-only facts of the element(s) selected on the sidebar's drawing, as label / value
// rows. The elevation tool's contextual tabs edit these; here they are only shown.
export function SelectionDetails(props: {app: ElevationApp; selection: MdSelection[]}) {
    const activeView = useEventEmitter2(props.app.viewSettings.activeView);
    const leaf = activeView === "location-inactive" || activeView === "door-inactive" ? "inactive" : "active";
    const facts = selectionFacts(props.app, props.selection, leaf);
    if(facts.length === 0) return null;

    return (
        <Box sx={{display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1.5, rowGap: 0.25, pt: 0.75}}>
            {facts.map(f => (
                <React.Fragment key={f.label}>
                    <Typography sx={{fontSize: 11, color: "text.secondary", lineHeight: 1.4}}>{f.label}</Typography>
                    <Typography sx={{fontSize: 11, lineHeight: 1.4}}>{f.value}</Typography>
                </React.Fragment>
            ))}
        </Box>
    );
}

function selectionFacts(app: ElevationApp, selection: MdSelection[], leaf: "active" | "inactive"): Fact[] {
    const fmt: Format = value => app.draw.formatDimension(value);
    const first = selection[0];
    const count = selection.length;

    switch(selectionGroupOf(first.type)) {
        case "stick": {
            if(count > 1 || first.type !== "stick") return [{label: "Sticks", value: String(count)}];
            const model = findStickById(app.frameSpec, pieceStickId(first.id));
            if(!model) return [];

            const out: Fact[] = [
                {label: "Orientation", value: first.orientation === "vertical" ? "Vertical" : "Horizontal"},
                {label: model.airGap ? "Gap" : "Thickness", value: fmt(stickThicknessVar(model, app.frameSpec).get())},
            ];
            if(model.slope) out.push({label: "Slope", value: `${fmt(model.slope.drop.get())} drop at end ${model.slope.shortEnd}`});
            if(model.angle) out.push({label: "Angle", value: `${model.angle.get()}°`});
            if(model.anchors) out.push(...anchorFacts(app, model.id, null, fmt));
            return out;
        }
        case "cell": {
            if(count > 1 || (first.type !== "lite" && first.type !== "panel")) return [{label: "Cells", value: String(count)}];
            const item = findItem(app.frameSpec, first.id);
            if(!item) return [];

            const out: Fact[] = [
                {label: "Size", value: `${fmt(first.size.width)} × ${fmt(first.size.height)}`},
                {label: "Profile", value: frameProfileLabels[itemProfile(app.frameSpec, item)]},
            ];
            if(item.kind === "lite") {
                out.push(
                    {label: "Mark", value: item.mark || "—"},
                    {label: "Glazing", value: glazingLabels[itemGlazing(item)]},
                    {label: "Glass", value: glassSummary(liteGlass(item), fmt)},
                );
            }
            if(item.kind === "fixedPanel") {
                out.push(
                    {label: "Material", value: panelMaterialLabels[itemPanelMaterial(item)]},
                    {label: "Position", value: panelPositionLabels[itemPanelPosition(item)]},
                );
            }
            return out;
        }
        case "door-lite": {
            const ids = selection.map(s => s.id);
            const lites = app.doorSpec.lites.filter(l => ids.includes(l.id));
            if(lites.length !== 1) return [{label: "Lites", value: String(lites.length)}];
            const lite = lites[0];

            return [
                {label: "Mark", value: lite.mark || "—"},
                {label: "Size", value: `${fmt(lite.width.get())} × ${fmt(lite.height.get())}`},
                {label: "Offset", value: `${fmt(lite.offsetLeft.get())} from left, ${fmt(lite.offsetTop.get())} from top`},
                {label: "Glass", value: glassSummary(doorLiteGlass(lite), fmt)},
            ];
        }
        case "hardware-item": {
            const hw = app.hardwareGroup?.hardware ?? [];
            const productIds = Array.from(new Set(selection.map(s => s.type === "hardware-item" ? s.productId : 0)));
            if(productIds.length !== 1 || first.type !== "hardware-item") return [{label: "Items", value: String(productIds.length)}];

            const item = hw.find(h => h.product === productIds[0]);
            const placement = app.hardware.list.find(p => p.id === first.id);
            const out: Fact[] = [
                {label: "Product", value: item?.productCode ?? "#" + productIds[0]},
            ];
            if(item?.category) out.push({label: "Category", value: item.category});
            if(item?.manufacturerName) out.push({label: "Manufacturer", value: item.manufacturerName});
            if(placement) {
                out.push(
                    {label: "On", value: placement.target === "door" ? "Door" : "Frame"},
                    {label: "Align", value: `${placement.alignToX} / ${placement.alignToY}`},
                    {label: "Position", value: `${fmt(placement.x.get())} from left, ${fmt(placement.y.get())} from top`},
                );
            }
            return out;
        }
        case "hinge": {
            const hinges = app.hardware.hinges;
            const hw = app.hardwareGroup?.hardware ?? [];
            const ids = selection.map(s => s.type === "hinge" ? s.hingeId : "");
            const selected = hinges.list.filter(h => ids.includes(h.id));
            if(selected.length === 0) return [];

            const defaultProduct = hinges.getDefaultProduct(hw);
            const codeOf = (product: number | null | undefined) => hw.find(h => h.product === product)?.productCode ?? (product ? "#" + product : "—");
            const out: Fact[] = [{label: selected.length === 1 ? "Hinge" : "Hinges", value: selected.map(h => h.id).join(" ")}];
            if(selected.length === 1) {
                const h = selected[0];
                const override = leaf === "active" ? h.activeProductOverride : h.inactiveProductOverride;
                out.push(
                    {label: "Product", value: override != null ? codeOf(override) : `${codeOf(defaultProduct)} (default)`},
                    {label: "From top", value: fmt(h.offsetTop.get())},
                );
            }
            return out;
        }
        case "void": {
            if(count > 1) return [{label: "Steps", value: String(count)}];
            const step = app.frameSpec.stepInfo.get(first.id);
            return step ? [{label: "Step height", value: fmt(step.height)}] : [];
        }
        case "door-slot": {
            const slot = slotOfCell(app.frameSpec, first.id);
            const item = findItem(app.frameSpec, first.id);
            if(!slot) return [];

            const out: Fact[] = [
                {label: "Slot", value: slot.name},
                {label: "Opening", value: slot.name === app.frameSpec.primarySlot ? (app.opening?.name ?? "—") : (app.slotOpening(slot.name)?.name ?? "None")},
            ];
            if(item?.kind === "doors") out.push({label: "Width", value: fmt(item.width.get())});
            return out;
        }
        case "anchor": {
            if(first.type !== "anchor-stick" && first.type !== "anchor-section") return [];
            return anchorFacts(app, first.stickId, first.type === "anchor-section" ? first.index : null, fmt);
        }
    }
}

// the anchors of an outline stick: all of its sections, or one of them.
function anchorFacts(app: ElevationApp, stickId: string, index: number | null, fmt: Format): Fact[] {
    const anchors = sticksById(app.frameSpec.frameBlocks).get(stickId)?.anchors;
    const layout = app.frameGrid?.anchorMarks.layoutOf(stickId) ?? null;
    if(!anchors) return [{label: "Anchors", value: "None"}];

    const products = app.anchorProducts.value?.products ?? [];
    const nameOf = (product: number) => {
        const p = products.find(x => x.id === product);
        return p ? `${p.name} (${p.productCode})` : "#" + product;
    };

    const sections = anchors.sections;
    const section = index !== null ? sections[index] : null;
    const resolved = index !== null ? layout?.sections[index] ?? null : null;

    if(section) {
        return [
            {label: "Section", value: `${index! + 1} of ${sections.length}`},
            {label: "Product", value: nameOf(section.product)},
            {label: "Qty", value: resolved ? `${resolved.qty}${resolved.auto ? " (auto)" : ""}` : section.qty?.toString() ?? "auto"},
            ...(resolved ? [{label: "Length", value: fmt(resolved.end - resolved.start)}] : []),
        ];
    }

    const sameProduct = sections.every(s => s.product === sections[0].product);
    const total = layout ? layout.sections.reduce((sum, s) => sum + s.qty, 0) : null;
    return [
        {label: "Anchor", value: sameProduct ? nameOf(sections[0].product) : "Mixed products"},
        {label: "Sections", value: String(sections.length)},
        ...(total !== null ? [{label: "Qty", value: String(total)}] : []),
    ];
}

function glassSummary(glass: MdGlassResolved, fmt: Format): string {
    const parts = [glassTypeLabels[glass.type], fmt(glass.thickness), `${glassBeadLabels[glass.bead]} bead`];
    if(glass.thermal) parts.unshift("Thermal");
    return parts.join(", ");
}
